import * as THREE from 'three';
import { resourceDefs, type ResourceDef } from '../data';

/**
 * Paylaşılan geometri/materyal fabrikası.
 * Her ağaç için yeni geometri üretmek mobilde hem belleği hem draw call'ları
 * patlatır; burada tür başına TEK geometri üretilip InstancedMesh ile çizilir.
 */

const geometries = new Map<string, THREE.BufferGeometry>();
const materials = new Map<number, THREE.Material>();

export function lambert(color: number, opts: { flatShading?: boolean; transparent?: boolean; opacity?: number } = {}): THREE.Material {
  const key = color * 31 + (opts.flatShading ? 1 : 0) * 7 + Math.round((opts.opacity ?? 1) * 100);
  let mat = materials.get(key);
  if (!mat) {
    mat = new THREE.MeshLambertMaterial({
      color,
      flatShading: opts.flatShading ?? true,
      transparent: opts.transparent ?? false,
      opacity: opts.opacity ?? 1,
    });
    materials.set(key, mat);
  }
  return mat;
}

/** Kaynak türüne göre tek parça (merged) geometri. */
export function resourceGeometry(def: ResourceDef): THREE.BufferGeometry {
  const cached = geometries.get(def.id);
  if (cached) return cached;

  let geo: THREE.BufferGeometry;
  switch (def.kind) {
    case 'tree': {
      // Düşük poligon ama simetrik konilerden daha doğal görünen gövde,
      // dal ve üç farklı yaprak kütlesi. Hâlâ tür başına tek instanced draw.
      const parts: THREE.BufferGeometry[] = [];
      const trunk = new THREE.CylinderGeometry(0.24, 0.36, 3.8, 7);
      trunk.translate(0, 1.9, 0);
      parts.push(trunk);
      for (const [x, y, z, sx, sy, sz] of [[-0.48, 4.05, 0.1, 1.25, 1.05, 1.1], [0.5, 4.55, -0.18, 1.15, 1.18, 1.1], [0.02, 5.45, 0.18, 1.0, 1.12, 0.95]] as const) {
        const canopy = new THREE.IcosahedronGeometry(1, 1);
        canopy.scale(sx, sy, sz);
        canopy.translate(x, y, z);
        parts.push(canopy);
      }
      geo = mergeGeometries(parts);
      break;
    }
    case 'rock': {
      geo = new THREE.DodecahedronGeometry(0.9, 0);
      geo.scale(1, 0.75, 1);
      geo.translate(0, 0.5, 0);
      break;
    }
    case 'ore': {
      const parts: THREE.BufferGeometry[] = [];
      const base = new THREE.DodecahedronGeometry(0.8, 0);
      base.scale(1, 0.7, 1);
      base.translate(0, 0.45, 0);
      parts.push(base);
      const crystal = new THREE.OctahedronGeometry(0.34, 0);
      crystal.translate(0.15, 0.85, 0.1);
      parts.push(crystal);
      geo = mergeGeometries(parts);
      break;
    }
    case 'bush': {
      geo = new THREE.IcosahedronGeometry(0.55, 0);
      geo.scale(1, 0.8, 1);
      geo.translate(0, 0.42, 0);
      break;
    }
    case 'sand': {
      geo = new THREE.ConeGeometry(0.7, 0.5, 6);
      geo.translate(0, 0.25, 0);
      break;
    }
    default: {
      // plant
      geo = new THREE.ConeGeometry(0.28, 0.7, 5);
      geo.translate(0, 0.35, 0);
      break;
    }
  }
  geo.computeVertexNormals();
  geometries.set(def.id, geo);
  return geo;
}

/** three'nin BufferGeometryUtils'ini çekmemek için minimal birleştirme. */
export function mergeGeometries(list: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  let vertexCount = 0;
  let indexCount = 0;
  for (const g of list) {
    const pos = g.getAttribute('position');
    vertexCount += pos.count;
    indexCount += g.index ? g.index.count : pos.count;
  }
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const indices = new Uint32Array(indexCount);
  let vo = 0;
  let io = 0;
  for (const g of list) {
    if (!g.getAttribute('normal')) g.computeVertexNormals();
    const pos = g.getAttribute('position') as THREE.BufferAttribute;
    const nor = g.getAttribute('normal') as THREE.BufferAttribute;
    positions.set(pos.array as Float32Array, vo * 3);
    normals.set(nor.array as Float32Array, vo * 3);
    if (g.index) {
      const src = g.index.array;
      for (let i = 0; i < src.length; i++) indices[io + i] = src[i]! + vo;
      io += src.length;
    } else {
      for (let i = 0; i < pos.count; i++) indices[io + i] = i + vo;
      io += pos.count;
    }
    vo += pos.count;
    g.dispose();
  }
  out.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  out.setIndex(new THREE.BufferAttribute(indices, 1));
  return out;
}

/** Kaynak türü için renk (ore ise maden rengi). */
export function resourceColor(def: ResourceDef): number {
  return def.color;
}

/** Yaprak rengi ağaç türüne göre değişir. */
export function canopyColorFor(id: string): number {
  switch (id) {
    case 'tree_pine':
      return 0x2c5e35;
    case 'tree_venom':
      return 0x7b3fb0;
    case 'tree_frost':
      return 0xdfeaf2;
    default:
      return 0x3e7d33;
  }
}

export function allResourceDefs(): ResourceDef[] {
  return resourceDefs.all();
}

/** Uygulama kapanırken/GPU baskısı azaltılırken çağrılabilir. */
export function disposeSharedAssets(): void {
  for (const g of geometries.values()) g.dispose();
  geometries.clear();
  for (const m of materials.values()) m.dispose();
  materials.clear();
}
